import React, { useState, useEffect, useRef } from 'react';
import { Card, Button, Badge, Form, Modal } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import Uppy from '@uppy/core';
import { Dashboard } from '@uppy/react';
import '@uppy/core/dist/style.css';
import '@uppy/dashboard/dist/style.css';
import { uploadVideoFile } from '@rd-web-markets/shared/dist/services/how_to_items_video_upload.service';
import { CancelButton, DeleteButton, EditButton, SaveButton } from '@rd-web-markets/shared/dist/util/buttons';

const HowToItemCard = ({ item, onUpdate, onDelete, onPlayVideo, readOnly = false }) => {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);
  const [showVideoUploadModal, setShowVideoUploadModal] = useState(false);
  const [uppyInstance, setUppyInstance] = useState(null);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const uppyRef = useRef(null);
  
  const [formData, setFormData] = useState({
    subject: item.subject || '',
    description: item.description || '',
    visibility: item.visibility || 'internal',
    is_internal: !!item.is_internal,
    is_external: !!item.is_external,
    pdf_file: null,
    remove_pdf_file: false,
  });

  useEffect(() => {
    setFormData({
      subject: item.subject || '',
      description: item.description || '',
      visibility: item.visibility || 'internal',
      pdf_file: null,
      is_internal: !!item.is_internal,
      is_external: !!item.is_external,
      remove_pdf_file: false,
    });
  }, [item]);

  useEffect(() => {
    if (showVideoUploadModal && !uppyRef.current) {
      const uppy = new Uppy({
        restrictions: {
          maxFileSize: 2 * 1024 * 1024 * 1024,
          allowedFileTypes: ['video/*'],
          maxNumberOfFiles: 1
        },
        autoProceed: false,
        debug: false
      });

      uppy.on('manual-progress-update', (file, percentage) => {
        const files = { ...uppy.getState().files };
        if (!files[file.id]) return;
        
        files[file.id] = {
          ...files[file.id],
          progress: {
            ...files[file.id].progress,
            uploadStarted: true,
            uploadComplete: false,
            percentage
          }
        };
        
        uppy.setState({ files });
      });

      uppy.on('upload-success', (file, response) => {
        const files = { ...uppy.getState().files };
        files[file.id] = {
          ...files[file.id],
          progress: {
            uploadComplete: true,
            percentage: 100
          }
        };
        uppy.setState({ files });
      });

      uppy.on('upload-error', (file, error) => {
        const files = { ...uppy.getState().files };
        if (files[file.id]) {
          files[file.id].error = error.message || 'Upload error';
        }
        uppy.setState({ files });
      });

      uppyRef.current = uppy;
      setUppyInstance(uppy);
    }

    if (!showVideoUploadModal && uppyRef.current) {
      uppyRef.current.cancelAll();
      
      if (typeof uppyRef.current.close === 'function') {
        uppyRef.current.close();
      }
      
      uppyRef.current = null;
      setUppyInstance(null);
    }
  }, [showVideoUploadModal]);

  const [saving, setSaving] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    const { name, files } = e.target;
    const file = files[0];
    setFormData((prev) => ({
      ...prev,
      [name]: file,
      [`remove_${name}`]: false,
    }));
  };

  const handleRemoveFile = (type) => {
    setFormData((prev) => ({
      ...prev,
      [`remove_${type}`]: true,
      [type]: null,
    }));
  };

  const canSave =
    formData.subject.trim() !== '' &&
    formData.description.trim() !== '' &&
    (formData.is_internal || formData.is_external);

  const handleSave = async () => {
    try {
      setSaving(true);
      await onUpdate({ id: item.id, data: formData });
      setEditing(false);
    } catch (err) {
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (window.confirm(t('confirm_delete'))) {
      await onDelete(item.id);
    }
  };

  const handleVideoUpload = async () => {
    if (!uppyInstance) return;
    
    const files = uppyInstance.getFiles();
    if (files.length === 0) {
      alert('Please select a video file');
      return;
    }

    try {
      setUploadingVideo(true);
      const file = files[0];

      await uploadVideoFile(uppyInstance, file, async (videoRecord) => {
        const updatePayload = {
          video_key: videoRecord.key,
          video_name: videoRecord.name,
          video_content_type: videoRecord.content_type
        };

        await onUpdate({id: item.id, data: updatePayload});
        
        setShowVideoUploadModal(false);
        uppyInstance.cancelAll();
        alert('Video uploaded successfully!');
      });
    } catch (error) {
      console.error('Failed to upload video:', error);
      alert('Video upload failed. Please try again.');
    } finally {
      setUploadingVideo(false);
    }
  };

  const pdf = item.pdf_download_link;
  const video = item.video_download_link;

  if (readOnly && editing) setEditing(false);

  return (
    <>
      <Card className="h-100 shadow-sm">
        <Card.Body className="d-flex flex-column">
          <div className="d-flex justify-content-between align-items-start mb-2">
            {!editing ? (
              <>
                <Card.Title>{item.subject}</Card.Title>
                {/* <div className="d-flex gap-1">
                  {item.is_internal && (
                    <Badge bg="primary">{t('internal')}</Badge>
                  )}
                  {item.is_external && (
                    <Badge bg="warning" text="dark">
                      {t('external')}
                    </Badge>
                  )}
                </div> */}
              </>
            ) : (
              <Form.Control
                name="subject"
                value={formData.subject}
                onChange={handleChange}
                placeholder={t('subject')}
                disabled={readOnly}
              />
            )}
          </div>

          {!editing ? (
            <Card.Text className="text-muted flex-grow-1">{item.description}</Card.Text>
          ) : (
            <Form.Control
              as="textarea"
              rows={3}
              name="description"
              value={formData.description}
              onChange={handleChange}
              placeholder={t('description')}
              className="flex-grow-1 mb-2"
              disabled={readOnly}
            />
          )}

          {editing && !readOnly && (
            <Form.Group controlId={`visibility_${item.id}`} className="mb-3">
              <Form.Label>{t('visibility')}</Form.Label>
              <div>
                <Form.Check
                  type="checkbox"
                  label={t('internal')}
                  name="is_internal"
                  checked={formData.is_internal}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, is_internal: e.target.checked }))
                  }
                />
                <Form.Check
                  type="checkbox"
                  label={t('external')}
                  name="is_external"
                  checked={formData.is_external}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, is_external: e.target.checked }))
                  }
                />
              </div>
            </Form.Group>
          )}

          {editing && !readOnly && (
            <>
              <Form.Group controlId={`pdf_file_${item.id}`} className="mb-3">
                <Form.Label>{t('pdf_file')}</Form.Label>
                <Form.Control
                  type="file"
                  name="pdf_file"
                  accept="application/pdf"
                  onChange={handleFileChange}
                />
                {pdf && (pdf.url || pdf.filename) && !formData.remove_pdf_file && !formData.pdf_file && (
                  <div className="mt-2">
                    <small className="text-muted">{pdf.filename}</small>
                    <Button
                      variant="outline-danger"
                      size="sm"
                      className="ms-2"
                      onClick={() => handleRemoveFile('pdf_file')}
                    >
                      {t('remove')}
                    </Button>
                  </div>
                )}
              </Form.Group>

              <div className="mb-3">
                <Form.Label>{t('video_file')}</Form.Label>
                <div className="d-flex align-items-center gap-2">
                  {video && video.url && (
                    <small className="text-muted">{video.filename}</small>
                  )}
                  <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={() => setShowVideoUploadModal(true)}
                  >
                    {video && video.url ? t('replace_video') : t('upload_video')}
                  </Button>
                </div>
              </div>
            </>
          )}

          {!editing && (
            <div className="d-flex gap-2 mb-2">
              {pdf && pdf.url && (
                <Button
                  variant="outline-danger"
                  size="sm"
                  as="a"
                  href={pdf.url}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {t('view_pdf')}
                </Button>
              )}
              {video && video.url && (
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={() => onPlayVideo({ url: video.url, title: item.subject })}
                >
                  {t('watch_video')}
                </Button>
              )}
            </div>
          )}

          {!readOnly && (
            <div className="d-flex justify-content-end gap-2 mt-auto">
              {!editing ? (
                <>
                  <EditButton onClick={() => setEditing(true)} />
                  <DeleteButton size="sm" onClick={handleDelete}>
                  </DeleteButton>
                </>
              ) : (
                <>
                  <SaveButton
                    onClick={handleSave}
                    disabled={saving || !canSave}
                    text={saving ? t('saving') : t('save')}
                  />
                  <CancelButton
                    onClick={() => {
                      setEditing(false);
                      setFormData({
                        subject: item.subject,
                        description: item.description,
                        visibility: item.visibility,
                        pdf_file: null,
                        is_internal: !!item.is_internal,
                        is_external: !!item.is_external,
                        remove_pdf_file: false,
                      });
                    }}
                    disabled={saving}
                  />
                </>
              )}
            </div>
          )}
        </Card.Body>
      </Card>

      <Modal
        show={showVideoUploadModal}
        onHide={() => !uploadingVideo && setShowVideoUploadModal(false)}
        size="lg"
      >
        <Modal.Header closeButton={!uploadingVideo}>
          <Modal.Title>{t('upload_video')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {uppyInstance && (
            <Dashboard
              uppy={uppyInstance}
              proudlyDisplayPoweredByUppy={false}
              showProgressDetails
              hideUploadButton
              width="100%"
              height="400px"
              theme="auto"
              note={t('video_upload_note')}
            />
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowVideoUploadModal(false)}
            disabled={uploadingVideo}
          >
            {t('cancel')}
          </Button>
          <Button
            variant="primary"
            onClick={handleVideoUpload}
            disabled={uploadingVideo}
          >
            {uploadingVideo ? t('uploading') : t('upload')}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default HowToItemCard;